import type { JSX } from 'react';
import { Alert, Badge, Center, Group, Loader, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type AiBackend, ApiError, aiSettingsApi } from '../api';
import { AI_EFFORTS, AI_MODELS, formatDateTime } from '../format';
import { Section } from '../ui/Section';

/**
 * Einstellungen › KI-Auswertung (ADR 0011, ADR 0022): which way answers the
 * jobs - Claude Code on the server or the Claude API -, whether it is
 * reachable, and with which model and effort.
 */
export function AiSettingsPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ['ai-settings'], queryFn: aiSettingsApi.status, refetchInterval: 20_000 });
  const save = useMutation({
    mutationFn: aiSettingsApi.save,
    onSettled: () => Promise.all([queryClient.invalidateQueries({ queryKey: ['ai-settings'] }), queryClient.invalidateQueries({ queryKey: ['offer-ai-status'] })]),
  });
  if (status.isPending) return <Center h={120}><Loader size="sm" /></Center>;
  if (status.isError) return <Alert color="red">{status.error instanceof ApiError ? status.error.message : 'Der Zustand ließ sich nicht laden.'}</Alert>;
  const s = status.data;
  const api = s.settings.backend === 'api';
  return (
    <Stack gap="md">
      <Section title="Weg" description="Wer die Aufträge beantwortet. Claude Code am Server nutzt das Claude-Abo des Servers (höchstens zwölf Aufträge am Tag, geteilt mit anderen Diensten). Die Claude-API schickt die Unterlagen direkt an Anthropic und wird je Auftrag abgerechnet; Kosten stehen am Ergebnis.">
        <SegmentedControl size="xs" w="fit-content" value={s.settings.backend} onChange={(v) => save.mutate({ ...s.settings, backend: v as AiBackend })}
          data={[{ value: 'server', label: 'Claude Code am Server' }, { value: 'api', label: 'Claude-API' }]} />
      </Section>
      {api ? (
        <Section title="Verbindung" description="Schlüssel und Prüfung unter Einstellungen › Verbindungen › Claude-API. Ohne Schlüssel bleibt die KI-Auswertung aus; alles andere geht weiter.">
          <Group gap="xs">
            {s.available ? <Badge color="green" variant="light">bereit</Badge> : <Badge color="orange" variant="light">nicht bereit</Badge>}
            <Text size="xs" c="dimmed">
              {s.api.keySet ? `Schlüssel eingetragen · heute ${s.api.today} von höchstens ${s.api.maxPerDay} Aufträgen` : 'kein Schlüssel eingetragen'}
            </Text>
          </Group>
          {!s.folder.writable && <Alert color="red" py={6}>Der Auftragsordner {s.folder.path} ist nicht beschreibbar – auf dem Server ops/ki/ki-einrichten.sh ausführen.</Alert>}
        </Section>
      ) : (
      <Section title="Verbindung" description="PST legt Aufträge im Auftragsordner ab; der Nutzerdienst pst-ki auf dem Server beantwortet sie mit Claude Code (nur Lesen im Ordner des Auftrags, keine Befehle). Ohne Lebenszeichen bleibt die KI-Auswertung aus; alles andere geht weiter.">
        <Group gap="xs">
          {s.available ? <Badge color="green" variant="light">erreichbar</Badge> : <Badge color="orange" variant="light">nicht erreichbar</Badge>}
          <Text size="xs" c="dimmed">
            {s.worker ? `letztes Lebenszeichen ${formatDateTime(s.worker.at)} · ${s.worker.state}${s.worker.job ? ` (${s.worker.job})` : ''}${s.worker.claude ? ` · ${s.worker.claude}` : ''}` : 'noch kein Lebenszeichen vom Server'}
          </Text>
        </Group>
        {s.worker?.state === 'Probe fehlgeschlagen' && <Alert color="red" py={6}>Die Verweigerungsprobe nach dem letzten Update von Claude Code ist nicht bestanden – der Server nimmt keine Aufträge an. Auf dem Server prüfen: ops/ki/pst-ki-worker.py --probe</Alert>}
        {!s.folder.writable && <Alert color="red" py={6}>Der Auftragsordner {s.folder.path} ist nicht beschreibbar – auf dem Server ops/ki/ki-einrichten.sh ausführen.</Alert>}
      </Section>
      )}
      <Section title="Modell und Aufwand" description={api
        ? 'Gilt für jeden neuen Auftrag. Gründlicher heißt langsamer und teurer: Fable kostet je Token etwa das Zweieinhalbfache von Opus und das Fünffache von Sonnet.'
        : 'Gilt für jeden neuen Auftrag. Gründlicher heißt langsamer; die Nutzung geht vom Claude-Abo des Servers ab.'}>
        <Group gap="sm">
          <Select size="xs" label="Modell" w={240} data={Object.entries(AI_MODELS).map(([value, label]) => ({ value, label }))} value={s.settings.model}
            onChange={(v) => v && save.mutate({ ...s.settings, model: v })} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
          <Select size="xs" label="Aufwand" w={160} data={Object.entries(AI_EFFORTS).map(([value, label]) => ({ value, label }))} value={s.settings.effort}
            onChange={(v) => v && save.mutate({ ...s.settings, effort: v })} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        </Group>
        {save.isError && <Alert color="red" py={6}>{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
      </Section>
    </Stack>
  );
}
